import { theme } from 'theme'
import styled, { CSSProp } from 'particles/themes/styled'

interface ButtonBaseProps {
    customStyle?: CSSProp
}
interface IconButtonWithCloseContainerProps extends ButtonBaseProps {
    isActive?: boolean
}
interface IconButton extends ButtonBaseProps {
    width: string
    height: string
    isActive?: boolean
    isDisable?: boolean
    reverseDisplay?: boolean
    fadeOnHover?: boolean
    disableFocusStyle?: boolean
}
interface ButtonContainerProps extends ButtonBaseProps {
    minWidth: string
    height: string
    padding: string
    fontSize: string
    isActive?: boolean
    isDisable?: boolean
    reverseDisplay?: boolean
    isIcon: boolean
}

export const ButtonContainer = styled.button<ButtonContainerProps>`
    font-family: inherit;
    min-width: ${({ minWidth }): string => minWidth};
    width: fit-content;
    height: ${({ height }): string => height};
    padding: ${({ padding }): string => padding};
    font-size: ${({ fontSize }): string => fontSize};
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    border-radius: 4px;
    border: 1px solid transparent;
    flex-direction: ${({ reverseDisplay }): string => (reverseDisplay ? 'row-reverse' : 'row')};
    cursor: pointer;
    text-decoration: none;
    font-family: inherit;
    .button-text {
        text-align: center;
        font-weight: ${theme.fontWeight.bold};
        margin-left: ${({ isIcon }): string | number => (isIcon ? theme.newSpacing.xxs : 0)};
        margin-right: ${({ isIcon, reverseDisplay }): string | number =>
            isIcon && reverseDisplay ? theme.newSpacing.xxs : 0};
    }
    background-color: ${theme.newColors.neutral50};
    color: ${theme.newColors.primary300};
    border-color: ${({ isActive }): string => (isActive ? theme.newColors.primary500 : theme.newColors.primary300)};
    &:hover {
        background-color: ${theme.newColors.primary50};
    }
    &:active {
        background-color: ${theme.newColors.primary50};
        border-color: ${theme.newColors.primary400};
        color: ${theme.newColors.primary400};
    }
    &:focus {
        background-color: ${theme.newColors.neutral50};
        border: 3px solid ${theme.newColors.primary200};
        color: ${theme.newColors.primary400};
    }
    &.disable {
        cursor: not-allowed;
    }
    &.primary {
        background-color: ${({ isActive }): string =>
            isActive ? theme.newColors.primary500 : theme.newColors.primary300};
        color: ${theme.newColors.neutral50};
        border-color: ${({ isActive }): string => (isActive ? theme.newColors.primary500 : theme.newColors.primary300)};
        &:hover {
            background-color: ${theme.newColors.primary200};
            border-color: ${theme.newColors.primary200};
        }
        &:active {
            background-color: ${theme.newColors.primary400};
            border-color: ${theme.newColors.primary400};
        }
        &:focus {
            background-color: ${theme.newColors.primary300};
            border: 3px solid ${theme.newColors.primary200};
        }
        &.disable {
            cursor: not-allowed;
            color: ${theme.newColors.neutral400};
            background-color: ${theme.newColors.neutral200};
            border-color: ${theme.newColors.neutral200};
        }
    }
    &.tertiary {
        background-color: ${theme.newColors.neutral50};
        color: ${theme.newColors.primary300};
        border: none;
        &:hover {
            background-color: ${theme.newColors.primary50};
        }
        &:active {
            background-color: ${theme.newColors.primary50};
            border-color: ${theme.newColors.primary400};
            color: ${theme.newColors.primary400};
        }
        &:focus {
            background-color: ${theme.newColors.neutral50};
            border: 3px solid ${theme.newColors.primary200};
            color: ${theme.newColors.primary400};
        }
        &.disable {
            color: ${theme.newColors.neutral400};
        }
    }
    &.secondary {
        background-color: ${theme.newColors.neutral50};
        color: ${({ isActive }): string => (isActive ? theme.newColors.primary500 : theme.newColors.primary300)};
        border-color: ${({ isActive }): string => (isActive ? theme.newColors.primary500 : theme.newColors.primary300)};
        &:hover {
            background-color: ${theme.newColors.primary50};
        }
        &:active {
            background-color: ${theme.newColors.primary50};
            border-color: ${theme.newColors.primary400};
            color: ${theme.newColors.primary400};
        }
        &:focus {
            background-color: ${theme.newColors.neutral50};
            border: 3px solid ${theme.newColors.primary200};
            color: ${theme.newColors.primary400};
        }
        &.disable {
            cursor: not-allowed;
            color: ${theme.newColors.neutral400};
            background-color: ${theme.newColors.neutral50};
            border-width: 1.5px;
            border-color: ${theme.newColors.neutral400};
        }
    }
    &.Filled {
        background-color: ${theme.newColors.neutral50};
        color: ${({ isActive }): string => (isActive ? theme.newColors.neutral500 : theme.newColors.primary300)};
        border-color: transparent;
        &:hover {
            background-color: ${theme.newColors.primary50};
        }
        &:active {
            background-color: ${theme.newColors.primary50};
            border-color: ${theme.newColors.primary400};
            color: ${theme.newColors.primary400};
        }
        &:focus {
            background-color: ${theme.newColors.neutral50};
            color: ${theme.newColors.primary400};
        }
        &.disable {
            cursor: not-allowed;
            color: ${theme.newColors.neutral400};
            background-color: ${theme.newColors.neutral50};
            border-width: 1.5px;
            border-color: ${theme.newColors.neutral400};
        }
    }
    &.outlined {
        background-color: ${theme.newColors.neutral50};
        color: ${({ isActive }): string => (isActive ? theme.newColors.neutral700 : theme.newColors.neutral500)};
        border-color: ${({ isActive }): string => (isActive ? theme.newColors.neutral600 : theme.newColors.neutral500)};
        &:hover {
            background-color: ${theme.newColors.neutral200};
        }
        &:active {
            color: ${theme.newColors.neutral700};
            border-color: ${theme.newColors.neutral600};
        }
        &:focus {
            color: ${theme.newColors.neutral700};
            border: 3px solid ${theme.newColors.neutral700};
        }
        &.disable {
            display: none;
        }
    }
    &.text {
        background-color: ${theme.newColors.neutral50};
        color: ${theme.newColors.neutral500};
        border-color: transparent;
        &:hover {
            background-color: ${theme.newColors.neutral200};
        }
        &:active {
            color: ${theme.newColors.neutral700};
        }
        &:focus {
            color: ${theme.newColors.neutral500};
            border: 1px solid ${theme.newColors.neutral400};
        }
        &.disable {
            cursor: not-allowed;
            color: ${theme.newColors.neutral400};
            border-color: transparent;
            &:hover {
                background-color: ${theme.newColors.neutral50};
            }
        }
    }
    &.customStyle {
        ${({ customStyle }): CSSProp | undefined => customStyle};
    }
`
export const IconButtonWithDescriptionContainer = styled.div<ButtonBaseProps>`
    width: 221px;
    display: flex;
    flex-direction: column;
    color: ${theme.newColors.neutral500};
    cursor: pointer;
    padding: 8px 4px;
    border-radius: 4px;
    &:hover {
        background-color: ${theme.newColors.neutral100};
    }
    &:active {
        background-color: ${theme.newColors.neutral100};
    }
    .title {
        svg {
            height: 16px;
            margin-right: 4px;
        }
        width: 100%;
        display: flex;
        align-items: center;
        font-weight: ${theme.fontWeight.bold};
        margin-bottom: 4px;
        font-size: ${theme.newFontSizes.s};
    }
    .description {
        width: 100%;
        word-wrap: break-word;
        line-height: 20px;
        font-size: ${theme.newFontSizes.s};
    }
`

export const IconButtonWithCloseContainer = styled.div<IconButtonWithCloseContainerProps>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: ${theme.newColors.neutral400};
    cursor: pointer;
    svg {
        height: ${theme.newFontSizes.m};
    }
    .close-icon {
        svg {
            path {
                stroke: ${theme.newColors.neutral400};
            }
        }
    }

    .title-area {
        width: calc(100% - 28px);
        display: flex;
        align-items: center;
        min-height: 28px;
        font-size: ${theme.newFontSizes.s};
        font-weight: ${theme.fontWeight.medium};
        color: ${({ isActive }): string => (isActive ? theme.colors.mineShaft : theme.newColors.neutral400)};
        .fill-can-change {
            margin-right: ${theme.newSpacing.xxxs};
            path {
                fill: ${({ isActive }): string => (isActive ? theme.colors.mineShaft : theme.newColors.neutral400)};
            }
        }
        .stroke-can-change {
            margin-right: ${theme.newSpacing.xxxs};
            path {
                stroke: ${({ isActive }): string => (isActive ? theme.colors.mineShaft : theme.newColors.neutral400)};
            }
        }
    }
`
export const IconButtonContainer = styled.button<IconButton>`
    width: ${({ width }): string => width};
    height: ${({ height }): string => height};
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    border-radius: 4px;
    border: 1px solid transparent;
    padding: 0;
    cursor: pointer;
    svg {
        height: 16px;
    }
    .button-text {
        text-align: center;
        font-weight: ${theme.fontWeight.bold};
    }
    background-color: ${theme.newColors.neutral50};
    color: ${theme.newColors.primary300};
    border-color: ${({ isActive }): string => (isActive ? theme.newColors.primary500 : theme.newColors.primary300)};
    &:hover {
        background-color: ${({ fadeOnHover }): any => fadeOnHover && theme.newColors.neutral50};
    }
    &:active {
        background-color: ${theme.newColors.primary50};
        border-color: ${theme.newColors.primary400};
        color: ${theme.newColors.primary400};
    }
    ${({ disableFocusStyle }) =>
        disableFocusStyle
            ? ''
            : `&:focus {
        background-color: ${theme.newColors.neutral50};
        border: 3px solid ${theme.newColors.primary200};
        color: ${theme.newColors.primary400};
    }`}
    &.primary {
        background-color: ${({ isActive }): string =>
            isActive ? theme.newColors.primary500 : theme.newColors.primary300};
        color: ${theme.newColors.neutral50};
        border-color: ${({ isActive }): string => (isActive ? theme.newColors.primary500 : theme.newColors.primary300)};
        &:hover {
            background-color: ${theme.newColors.primary200};
            border-color: ${theme.newColors.primary200};
        }
        &:active {
            background-color: ${theme.newColors.primary400};
            border-color: ${theme.newColors.primary400};
        }
        &:focus {
            background-color: ${theme.newColors.primary300};
            border: 3px solid ${theme.newColors.primary200};
        }
    }
    &.tertiary {
        background-color: ${theme.newColors.neutral50};
        color: ${theme.newColors.primary300};
        border: none;
        &:hover {
            background-color: ${theme.newColors.primary50};
        }
        &:active {
            background-color: ${theme.newColors.primary50};
            border-color: ${theme.newColors.primary400};
            color: ${theme.newColors.primary400};
        }
        &:focus {
            background-color: ${theme.newColors.neutral50};
            border: 3px solid ${theme.newColors.primary200};
            color: ${theme.newColors.primary400};
        }
    }
    &.secondary {
        background-color: ${theme.newColors.neutral50};
        color: ${theme.newColors.primary300};
        border-color: ${({ isActive }): string => (isActive ? theme.newColors.primary500 : theme.newColors.primary300)};
        &:hover {
            background-color: ${theme.newColors.primary50};
        }
        &:active {
            background-color: ${theme.newColors.primary50};
            border-color: ${theme.newColors.primary400};
            color: ${theme.newColors.primary400};
        }
        &:focus {
            background-color: ${theme.newColors.neutral50};
            border: 3px solid ${theme.newColors.primary200};
            color: ${theme.newColors.primary400};
        }
    }
    &.disable {
        background-color: ${theme.newColors.primary300};
    }
    ${({ customStyle }): CSSProp | undefined => customStyle};
`
export const ImageButtonWithDescriptionContainer = styled.div<ButtonBaseProps>`
    width: 100%;
    max-width: 300px;
    min-height: 80px;
    display: flex;
    color: ${theme.newColors.neutral500};
    cursor: pointer;
    padding: ${theme.newSpacing.xxs} ${theme.newSpacing.xxxxs};
    box-sizing: border-box;
    border-radius: ${theme.newSpacing.xxxs};
    color: ${theme.newColors.neutral500};
    font-size: ${theme.newFontSizes.s};
    transition: background 150ms ease-in-out;
    .image {
        height: 42px;
        width: 42px;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: ${theme.newColors.neutral100};
        border-radius: 4px;
        margin-right: ${theme.newSpacing.xxs};
    }
    .description {
        width: 242px;
    }
    .image-title {
        font-weight: ${theme.fontWeight.medium};
        margin-bottom: ${theme.newSpacing.xxxxs};
    }
    .image-description {
        line-height: ${theme.newFontSizes.xl};
    }
    &:hover {
        background-color: ${theme.newColors.neutral100};
    }
    &:active {
        background-color: ${theme.newColors.neutral100};
    }
`
